/**
 * The "Remove Cut" confirmation: asks before a line's cut-to-size settings are removed. A modal
 * on desktop, a slide-in panel on mobile.
 *
 * Position: each page that shows cuts owns one instance and opens it from the "Cut To" box's
 * Remove Cut link: `components/ShoppingCart/ShoppingCart.tsx`,
 * `components/ShoppingListDetails/ShoppingListDetails.tsx`, `components/Catalog/CatalogItems.tsx`,
 * `components/Search/Search.tsx`, `components/Product/ProductDetails.tsx` and
 * `components/QuoteDetails/QuoteDetails.tsx`. The removal itself is the page's `onDelete`.
 *
 * Rules a change must keep: on confirm, the modal closes, `onDelete(index, ryersonSku)` runs, and
 * the GA `cut_to_size` "remove" event is sent with the SKU. Cancel and close only close.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import { RemoveDefineCutContent, RemoveDefineCutText } from "./RemoveCutModalContent";
import RemoveCutModalDesktop from "./RemoveCutModalDesktop";
import RemoveCutModalMobile from "./RemoveCutModalMobile";
import {
	GoogleAnalyticsEventName,
	GoogleAnalyticsEventActionNames,
	sendGAEvent,
} from "@ryerson/frontend.common";

/** Props of {@link RemoveCutModal}. */
export type RemoveCutModalProps = {
	/** The row whose cut is removed, passed back to `onDelete`. */
	index: number;
	showModal: boolean;
	setShowModal: (value: boolean) => void;
	/** Removes the cut. Called with `index` and `ryersonSku` (or `""`). */
	onDelete: (index: number, value: string) => void;
	/**
	 * The line's SKU (callers pass the product's `materialMaster`), for `onDelete` and the GA
	 * event.
	 */
	ryersonSku?: string;
};

/** Props of the two views, `RemoveCutModalDesktop` and `RemoveCutModalMobile`. */
export type RemoveCutModalType = {
	content: RemoveDefineCutText;
	showModal: boolean;
	setShowModal?: (value: boolean) => void;
	handleRemoveCut: () => void;
	handleClose: () => void;
};

/** Confirms and performs a cut removal. See the file header. */
const RemoveCutModal: React.FC<RemoveCutModalProps> = ({
	index,
	showModal,
	setShowModal,
	onDelete,
	ryersonSku,
}) => {
	const {
		localization: { language },
	} = useApplication();

	const content: RemoveDefineCutText =
		RemoveDefineCutContent[language] || RemoveDefineCutContent.en;

	const handleRemoveCut = () => {
		setShowModal(false);
		onDelete(index, ryersonSku || "");

		// send google analytics event on remove cut confirm
		sendGAEvent(GoogleAnalyticsEventName.CUT_TO_SIZE, {
			action: GoogleAnalyticsEventActionNames.REMOVE,
			item_id: ryersonSku || "",
		});
	};

	const handleClose = () => {
		setShowModal(false);
	};

	return (
		<>
			<Media lessThan="lg">
				<RemoveCutModalMobile
					content={content}
					showModal={showModal}
					setShowModal={setShowModal}
					handleRemoveCut={handleRemoveCut}
					handleClose={handleClose}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<RemoveCutModalDesktop
					content={content}
					showModal={showModal}
					handleRemoveCut={handleRemoveCut}
					handleClose={handleClose}
				/>
			</Media>
		</>
	);
};
export default RemoveCutModal;
